/**
 * SendSettings — channel pills for the composer's "how this reaches
 * the customer" decision. Section 4 of the composer per
 * `11-new-invoice-screen-spec.md` §9.
 *
 * Channels:
 *   - `'issue-only'`   — issue without dispatching (operator will
 *     copy/paste or print on their own).
 *   - `'whatsapp'`     — primary path; bus enqueues via Meta API.
 *   - `'email'`        — when the customer prefers email; bus
 *     enqueues via Resend.
 *   - `'print-later'`  — intent log only, no dispatch.
 *
 * Each option is disabled when the customer doesn't have the
 * required handle (no phone → WhatsApp/SMS dimmed; no email →
 * Email dimmed). The composer reads `customer` to compute these
 * disabled states + passes them down.
 *
 * The "SMS" channel from the dispatch contract isn't surfaced here
 * — operators who want SMS can pick "Print later" and copy the
 * link, or the V1.x SMS path lands its own affordance.
 */
import { MessageCircle, Mail, Printer, FileText } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card, CardBody } from "@flint/ui";
import { SectionHeading } from "./section-heading.tsx";
import { AiHint } from "./ai-hint.tsx";

export type ComposerSendChannel = "issue-only" | "whatsapp" | "email" | "print-later";

export interface SendSettingsProps {
  value: ComposerSendChannel;
  onChange: (next: ComposerSendChannel) => void;
  /** When true, WhatsApp is dimmed with a "no phone on file" hint. */
  customerHasPhone: boolean;
  customerHasEmail: boolean;
  disabled?: boolean;
}

interface Channel {
  value: ComposerSendChannel;
  label: string;
  /** Inline helper rendered as `· <helper>` next to the label.
   *  Empty string suppresses the helper (matches the prototype's
   *  "Print later" pill which has no sub-text). */
  helper: string;
  icon: LucideIcon;
}

const CHANNELS: Channel[] = [
  {
    value: "issue-only",
    label: "Issue only",
    helper: "no send",
    icon: FileText,
  },
  {
    value: "whatsapp",
    label: "WhatsApp",
    helper: "customer default",
    icon: MessageCircle,
  },
  {
    value: "email",
    // Email-on-file lookup waits on the customer-list contract — until
    // then the helper toggles between '' (no email) and 'on file' once
    // the listRow surfaces it. See TODO in `customer-picker.tsx`.
    label: "Email",
    helper: "",
    icon: Mail,
  },
  {
    value: "print-later",
    label: "Print later",
    helper: "",
    icon: Printer,
  },
];

export function SendSettings({
  value,
  onChange,
  customerHasPhone,
  customerHasEmail,
  disabled,
}: SendSettingsProps) {
  return (
    <Card>
      <SectionHeading
        step={4}
        title="Send settings"
        description="How the invoice reaches the customer."
      />
      <CardBody className="flex flex-col gap-3.5">
        <fieldset
          disabled={disabled}
          role="radiogroup"
          aria-label="Send channel"
          className="flex flex-wrap gap-2"
        >
          <legend className="sr-only">Send channel</legend>
          {CHANNELS.map((c) => {
            const noPhone = c.value === "whatsapp" && !customerHasPhone;
            const noEmail = c.value === "email" && !customerHasEmail;
            const dimmed = noPhone || noEmail;
            const active = value === c.value;
            // Inline helper text — mirrors the prototype's `<small>·
            // customer default</small>` shape. When dimmed for a
            // missing handle, swap to a clear "no X on file" note.
            const helper = noPhone ? "no phone on file" : noEmail ? "no email on file" : c.helper;
            return (
              <button
                key={c.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => onChange(c.value)}
                disabled={dimmed}
                className={[
                  "inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm transition-colors",
                  active
                    ? "border-sky-500 bg-sky-50 text-sky-700"
                    : "border-line bg-paper text-ink-2 hover:bg-raised",
                  dimmed ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
                ].join(" ")}
              >
                <c.icon size={14} aria-hidden />
                <span className="font-medium">{c.label}</span>
                {helper ? <span className="text-muted text-xs font-normal">· {helper}</span> : null}
              </button>
            );
          })}
        </fieldset>

        {/* Deferred AI hint — best send-window analytics ship in V1.x. */}
        <AiHint
          body={
            <>
              Best send-window suggestions (e.g. "Tue–Thu, 11am–12pm") appear here once we have
              enough customer reply-time data (V1.x).
            </>
          }
        />
      </CardBody>
    </Card>
  );
}
